import React from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  BadgeDollarSign, 
  BadgeEuro, 
  BadgePoundSterling, 
  BadgeJapaneseYen, 
  BadgeIndianRupee,
  CircleDollarSign,
  Currency
} from "lucide-react";

interface CurrencySelectProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

// Comprehensive list of currencies used in EU tenders
const currencies = [
  // Main currencies with icons
  { code: "EUR", name: "Euro", icon: <BadgeEuro className="h-4 w-4" /> },
  { code: "USD", name: "US Dollar", icon: <BadgeDollarSign className="h-4 w-4" /> },
  { code: "GBP", name: "British Pound", icon: <BadgePoundSterling className="h-4 w-4" /> },
  { code: "JPY", name: "Japanese Yen", icon: <BadgeJapaneseYen className="h-4 w-4" /> },
  { code: "INR", name: "Indian Rupee", icon: <BadgeIndianRupee className="h-4 w-4" /> },
  
  // EU currencies
  { code: "BGN", name: "Bulgarian Lev", icon: <Currency className="h-4 w-4" /> },
  { code: "CZK", name: "Czech Koruna", icon: <Currency className="h-4 w-4" /> },
  { code: "DKK", name: "Danish Krone", icon: <Currency className="h-4 w-4" /> },
  { code: "HUF", name: "Hungarian Forint", icon: <Currency className="h-4 w-4" /> },
  { code: "PLN", name: "Polish Złoty", icon: <Currency className="h-4 w-4" /> },
  { code: "RON", name: "Romanian Leu", icon: <Currency className="h-4 w-4" /> },
  { code: "SEK", name: "Swedish Krona", icon: <Currency className="h-4 w-4" /> },
  { code: "HRK", name: "Croatian Kuna", icon: <Currency className="h-4 w-4" /> },
  
  // Other common currencies
  { code: "AED", name: "UAE Dirham", icon: <CircleDollarSign className="h-4 w-4" /> },
  { code: "AUD", name: "Australian Dollar", icon: <CircleDollarSign className="h-4 w-4" /> },
  { code: "CAD", name: "Canadian Dollar", icon: <CircleDollarSign className="h-4 w-4" /> },
  { code: "CHF", name: "Swiss Franc", icon: <CircleDollarSign className="h-4 w-4" /> },
  { code: "CNY", name: "Chinese Yuan", icon: <CircleDollarSign className="h-4 w-4" /> },
  { code: "NOK", name: "Norwegian Krone", icon: <CircleDollarSign className="h-4 w-4" /> },
  { code: "NZD", name: "New Zealand Dollar", icon: <CircleDollarSign className="h-4 w-4" /> },
  { code: "RUB", name: "Russian Ruble", icon: <CircleDollarSign className="h-4 w-4" /> },
  { code: "SGD", name: "Singapore Dollar", icon: <CircleDollarSign className="h-4 w-4" /> },
  { code: "ZAR", name: "South African Rand", icon: <CircleDollarSign className="h-4 w-4" /> },
  
  // Additional currencies
  { code: "AFN", name: "Afghani", icon: null },
  { code: "ALL", name: "Lek", icon: null },
  { code: "AMD", name: "Dram", icon: null },
  { code: "ANG", name: "Netherlands Antillean Guilder", icon: null },
  { code: "AOA", name: "Kwanza", icon: null },
  { code: "ARS", name: "Argentine Peso", icon: null },
  { code: "AWG", name: "Aruban Guilder", icon: null },
  { code: "AZN", name: "Azerbaijan Manat", icon: null },
  { code: "BAM", name: "Convertible Mark", icon: null },
  { code: "BBD", name: "Barbados Dollar", icon: null },
  { code: "BDT", name: "Taka", icon: null },
  { code: "BHD", name: "Bahraini Dinar", icon: null },
  { code: "BIF", name: "Burundi Franc", icon: null },
  { code: "BMD", name: "Bermuda Dollar", icon: null },
  { code: "BND", name: "Brunei Dollar", icon: null },
  { code: "BOB", name: "Boliviano", icon: null },
  { code: "BRL", name: "Real", icon: null },
  { code: "BSD", name: "Bahamian Dollar", icon: null },
  { code: "BTN", name: "Ngultrum", icon: null },
  { code: "BWP", name: "Pula", icon: null },
  { code: "BYN", name: "Belarusian Rouble", icon: null },
  { code: "BZD", name: "Belize Dollar", icon: null },
  { code: "CDF", name: "Congolese Franc", icon: null },
  { code: "CLP", name: "Chilean Peso", icon: null },
  { code: "COP", name: "Colombian Peso", icon: null },
  { code: "CRC", name: "Costa Rican Colon", icon: null },
  { code: "CUC", name: "Convertible Peso", icon: null },
  { code: "CUP", name: "Cuban Peso", icon: null },
  { code: "CVE", name: "Cabo Verde Escudo", icon: null },
  { code: "DJF", name: "Djibouti Franc", icon: null },
  { code: "DOP", name: "Dominican Peso", icon: null },
  { code: "DZD", name: "Algerian Dinar", icon: null },
  { code: "EGP", name: "Egyptian Pound", icon: null },
  { code: "ERN", name: "Nakfa", icon: null },
  { code: "ETB", name: "Birr", icon: null },
  { code: "FJD", name: "Fiji Dollar", icon: null },
  { code: "FKP", name: "Falkland Islands Pound", icon: null },
  { code: "GEL", name: "Lari", icon: null },
  { code: "GHS", name: "Ghana Cedi", icon: null },
  { code: "GIP", name: "Gibraltar Pound", icon: null },
  { code: "GMD", name: "Dalasi", icon: null },
  { code: "GNF", name: "Guinean Franc", icon: null },
  { code: "GTQ", name: "Quetzal", icon: null },
  { code: "GYD", name: "Guyana Dollar", icon: null },
  { code: "HKD", name: "Hong Kong Dollar", icon: null },
  { code: "HNL", name: "Lempira", icon: null },
  { code: "HTG", name: "Gourde", icon: null },
  { code: "IDR", name: "Rupiah", icon: null },
  { code: "ILS", name: "Shekel", icon: null },
  { code: "IQD", name: "Iraqi Dinar", icon: null },
  { code: "IRR", name: "Iranian Rial", icon: null },
  { code: "ISK", name: "Iceland Króna", icon: null },
  { code: "JMD", name: "Jamaica Dollar", icon: null },
  { code: "JOD", name: "Jordanian Dinar", icon: null },
  { code: "KES", name: "Kenyan Shilling", icon: null },
  { code: "KGS", name: "Som", icon: null },
  { code: "KHR", name: "Riel", icon: null },
  { code: "KMF", name: "Comorian Franc", icon: null },
  { code: "KPW", name: "North Korean Won", icon: null },
  { code: "KRW", name: "South Korean Won", icon: null },
  { code: "KWD", name: "Kuwaiti Dinar", icon: null },
  { code: "KYD", name: "Cayman Islands Dollar", icon: null },
  { code: "KZT", name: "Tenge", icon: null },
  { code: "LAK", name: "Kip", icon: null },
  { code: "LBP", name: "Lebanese Pound", icon: null },
  { code: "LKR", name: "Sri Lankan Rupee", icon: null },
  { code: "LRD", name: "Liberian Dollar", icon: null },
  { code: "LSL", name: "Loti", icon: null },
  { code: "LYD", name: "Libyan Dinar", icon: null },
  { code: "MAD", name: "Moroccan Dirham", icon: null },
  { code: "MDL", name: "Moldovan Leu", icon: null },
  { code: "MGA", name: "Ariary", icon: null },
  { code: "MKD", name: "Denar", icon: null },
  { code: "MMK", name: "Kyat", icon: null },
  { code: "MNT", name: "Tugrik", icon: null },
  { code: "MOP", name: "Pataca", icon: null },
  { code: "MRO", name: "Ouguiya (pre-2018)", icon: null },
  { code: "MRU", name: "Ouguiya", icon: null },
  { code: "MUR", name: "Mauritian Rupee", icon: null },
  { code: "MVR", name: "Rufiyaa", icon: null },
  { code: "MWK", name: "Malawian Kwacha", icon: null },
  { code: "MXN", name: "Mexican Peso", icon: null },
  { code: "MYR", name: "Ringgit", icon: null },
  { code: "MZN", name: "Metical", icon: null },
  { code: "NAD", name: "Namibian Dollar", icon: null },
  { code: "NGN", name: "Naira", icon: null },
  { code: "NIO", name: "Córdoba Oro", icon: null },
  { code: "NPR", name: "Nepalese Rupee", icon: null },
  { code: "OMR", name: "Omani Rial", icon: null },
  { code: "PAB", name: "Balboa", icon: null },
  { code: "PEN", name: "Sol", icon: null },
  { code: "PGK", name: "Kina", icon: null },
  { code: "PHP", name: "Philippine Peso", icon: null },
  { code: "PKR", name: "Pakistani Rupee", icon: null },
  { code: "PYG", name: "Guaraní", icon: null },
  { code: "QAR", name: "Qatari Rial", icon: null },
  { code: "RSD", name: "Serbian Dinar", icon: null },
  { code: "RWF", name: "Rwandese Franc", icon: null },
  { code: "SAR", name: "Saudi Riyal", icon: null },
  { code: "SBD", name: "Solomon Islands Dollar", icon: null },
  { code: "SCR", name: "Seychelles Rupee", icon: null },
  { code: "SDG", name: "Sudanese Pound", icon: null },
  { code: "SHP", name: "Saint Helena Pound", icon: null },
  { code: "SLE", name: "Leone", icon: null },
  { code: "SLL", name: "Leone (pre-2022)", icon: null },
  { code: "SOS", name: "Somali Shilling", icon: null },
  { code: "SRD", name: "Surinamese Dollar", icon: null },
  { code: "SSP", name: "South Sudanese Pound", icon: null },
  { code: "STD", name: "Dobra (pre-2018)", icon: null },
  { code: "STN", name: "Dobra", icon: null },
  { code: "SVC", name: "Salvadorian Colón", icon: null },
  { code: "SZL", name: "Lilangeni", icon: null },
  { code: "THB", name: "Baht", icon: null },
  { code: "TJS", name: "Somoni", icon: null },
  { code: "TMT", name: "Turkmen Manat", icon: null },
  { code: "TND", name: "Tunisian Dinar", icon: null },
  { code: "TOP", name: "Pa'anga", icon: null },
  { code: "TRY", name: "Turkish Lira", icon: null },
  { code: "TTD", name: "Trinidad and Tobago Dollar", icon: null },
  { code: "TWD", name: "New Taiwan Dollar", icon: null },
  { code: "TZS", name: "Tanzanian Shilling", icon: null },
  { code: "UAH", name: "Hryvnia", icon: null },
  { code: "UGX", name: "Uganda Shilling", icon: null },
  { code: "USN", name: "US Dollar (Next day)", icon: null },
  { code: "UYU", name: "Uruguayan Peso", icon: null },
  { code: "UZS", name: "Sum", icon: null },
  { code: "VES", name: "Bolívar Soberano", icon: null },
  { code: "VND", name: "Dong", icon: null },
  { code: "VUV", name: "Vatu", icon: null },
  { code: "WST", name: "Tala", icon: null },
  { code: "XAF", name: "CFA Franc BEAC", icon: null },
  { code: "XCD", name: "East Caribbean Dollar", icon: null },
  { code: "XOF", name: "CFA Franc BCEAO", icon: null },
  { code: "XPF", name: "CFP Franc", icon: null },
  { code: "YER", name: "Yemeni Rial", icon: null },
  { code: "ZMW", name: "Zambian Kwacha", icon: null }
];

export const CurrencySelect: React.FC<CurrencySelectProps> = ({ value, onChange, disabled = false }) => {
  return (
    <Select 
      value={value} 
      onValueChange={onChange}
      disabled={disabled}
    >
      <SelectTrigger className="w-full">
        <SelectValue placeholder="Select currency" />
      </SelectTrigger>
      <SelectContent className="max-h-[300px]">
        {currencies.map((currency) => (
          <SelectItem key={currency.code} value={currency.code}>
            <div className="flex items-center">
              {currency.icon && <span className="mr-2">{currency.icon}</span>}
              <span>{currency.code} - {currency.name}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
